import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { JobRole, JobRoleSapSyncSummary, SapIgnoredJob } from '@hcc/types';

/**
 * The master job-role list. `useRoles()` returns active roles (for the person
 * and template pickers); the admin page uses `includeArchived` to manage all.
 */

const KEY = ['roles'] as const;
const ALL_KEY = ['roles', 'all'] as const;

export function useRoles() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await api<{ roles: JobRole[] }>('/api/admin/roles')).roles,
  });
}

export function useAllRoles() {
  return useQuery({
    queryKey: ALL_KEY,
    queryFn: async () =>
      (await api<{ roles: JobRole[] }>('/api/admin/roles?include_archived=true')).roles,
  });
}

export interface RoleInput {
  name: string;
  external_ref?: string | null;
  category_id?: string | null;
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ALL_KEY });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<RoleInput>) =>
      api<{ role: JobRole }>(`/api/admin/roles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useArchiveRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ role: JobRole }>(`/api/admin/roles/${id}/archive`, { method: 'POST' }),
    onSuccess: () => invalidate(qc),
  });
}

export function useRestoreRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ role: JobRole }>(`/api/admin/roles/${id}/restore`, { method: 'POST' }),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Free-typed role from the wizard combobox: returns the existing role for a
 * known name, otherwise creates it as pending SAP creation (and the service
 * desk is notified server-side).
 */
export function useRequestRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ role: JobRole; created: boolean }>('/api/admin/roles/request', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: (res) => {
      if (res.created) invalidate(qc);
    },
  });
}

/** One-click pull of the SAP job list + labour resources (read-only to SAP). */
export function useSapRoleSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ summary: JobRoleSapSyncSummary }>('/api/admin/roles/sap-sync', { method: 'POST' }),
    onSuccess: () => invalidate(qc),
  });
}

const IGNORED_KEY = ['roles', 'sap-ignored-jobs'] as const;

/** SAP jobs archived in the app (duplicates SAP itself cannot delete). */
export function useSapIgnoredJobs() {
  return useQuery({
    queryKey: IGNORED_KEY,
    queryFn: async () =>
      (await api<{ jobs: SapIgnoredJob[] }>('/api/admin/roles/sap-ignored-jobs')).jobs,
  });
}

/**
 * Archive a SAP job, then re-run the sync so the duplicate resolves (and a
 * role linked to the archived job re-links to its surviving twin) in one go.
 */
export function useArchiveSapJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (job: { job_id: string; name: string }) => {
      await api('/api/admin/roles/sap-ignored-jobs', {
        method: 'POST',
        body: JSON.stringify(job),
      });
      return api<{ summary: JobRoleSapSyncSummary }>('/api/admin/roles/sap-sync', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: IGNORED_KEY });
    },
  });
}

/** Restore an archived SAP job and re-sync so it reappears immediately. */
export function useRestoreSapJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      await api(`/api/admin/roles/sap-ignored-jobs/${encodeURIComponent(jobId)}`, {
        method: 'DELETE',
      });
      return api<{ summary: JobRoleSapSyncSummary }>('/api/admin/roles/sap-sync', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      invalidate(qc);
      qc.invalidateQueries({ queryKey: IGNORED_KEY });
    },
  });
}

export interface RolesImportReport {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
  dry_run: boolean;
}

export function useImportRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { csv: string; dry_run?: boolean }) =>
      api<RolesImportReport>('/api/admin/roles/import', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    // A dry-run writes nothing, so the cached list is still accurate.
    onSuccess: (report) => {
      if (!report.dry_run) invalidate(qc);
    },
  });
}
